import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { createUser, searchUserByPhone, searchUserByEmail } from '../services/userApi';
import countryData from '../data/country.json';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const EventRegistrationPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    gender: '',
    preferredLanguage: i18n.language,
    country: '',
    address: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [nationSearch, setNationSearch] = useState('');
  const [isNationDropdownOpen, setIsNationDropdownOpen] = useState(false);
  const nationDropdownRef = useRef(null);
  const [isPhoneChecking, setIsPhoneChecking] = useState(false);
  const [isEmailChecking, setIsEmailChecking] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

   const languages = [
     { code: 'en', name: t('languages.en', 'English') },
    { code: 'te', name: t('languages.te', 'తెలుగు') },
    { code: 'or', name: t('languages.or', 'ଓଡ଼ିଆ') },
    { code: 'kn', name: t('languages.kn', 'ಕನ್ನಡ') },
    { code: 'ml', name: t('languages.ml', 'മലയാളം') },
    { code: 'bn', name: t('languages.bn', 'বাংলা') },
    { code: 'mr', name: t('languages.mr', 'मराठी') },
    { code: 'gu', name: t('languages.gu', 'ગુજરાતી') },
    { code: 'ta', name: t('languages.ta', 'தமிழ்') },
    { code: 'hi', name: t('languages.hi', 'हिंदी') },
  ];

  const nations = useMemo(() =>
    Object.entries(countryData.data)
      .map(([code, { country }]) => ({ code, name: country }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nationDropdownRef.current && !nationDropdownRef.current.contains(event.target)) {
        setIsNationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      // The page will re-render with the form for a new registration.
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const { mutateAsync: createUserMutation, isLoading } = useMutation(createUser);

  const calculatePasswordStrength = (password) => {
    if (!password) return 0;

    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSymbols = /[^a-zA-Z\d]/.test(password);
    
    let score = 0;
    if (hasLetters) score++;
    if (hasNumbers) score++;
    if (hasSymbols) score++;

    if (password.length < 8) return 1; // Very weak
    if (score === 1) return 2; // Weak
    if (score === 2) return 3; // Medium
    if (score === 3) return 4; // Strong
    
    return 1; // Fallback
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
    if (name === 'email') {
      setIsEmailValid(false);
      setFormErrors(errors => ({ ...errors, email: undefined }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
    setIsPhoneValid(false);
    setFormErrors(errors => ({ ...errors, phone: undefined }));
  };

  const handleNationSearchChange = (e) => {
    setNationSearch(e.target.value);
    setIsNationDropdownOpen(true);
    if (formData.country) {
        setFormData({ ...formData, country: '' });
    }
  };

  const handleNationSelect = (nation) => {
    setFormData({ ...formData, country: nation.code });
    setNationSearch(nation.name);
    setIsNationDropdownOpen(false);
  };

  const handleEmailBlur = async () => {
    const email = formData.email;
    setIsEmailValid(false);
    if (email && !/\S+@\S+\.\S+/.test(email)) {
        setFormErrors(errors => ({ ...errors, email: 'Please enter a valid email address.' }));
        return;
    }
    if (email) {
      setIsEmailChecking(true);
      setFormErrors(errors => ({ ...errors, email: undefined }));
      try {
        const existingUsers = await searchUserByEmail(email);
        if (existingUsers && existingUsers.length > 0) {
          setFormErrors(errors => ({ ...errors, email: 'This email is already registered. Please try logging in.' }));
        } else {
          setIsEmailValid(true);
        }
      } catch (error) {
        console.error('Email validation failed:', error);
      } finally {
        setIsEmailChecking(false);
      }
    }
  };

  const handlePhoneBlur = async () => {
    const phone = formData.phone;
    setIsPhoneValid(false);
    if (phone && isValidPhoneNumber(phone)) {
      setIsPhoneChecking(true);
      setFormErrors(errors => ({ ...errors, phone: undefined }));
      try {
        const existingUsers = await searchUserByPhone(phone);
        if (existingUsers && existingUsers.length > 0) {
          setFormErrors(errors => ({ ...errors, phone: 'This phone number is already registered.' }));
        } else {
          setIsPhoneValid(true);
        }
      } catch (error) {
        console.error('Phone validation failed:', error);
      } finally {
        setIsPhoneChecking(false);
      }
    }
  };

  const filteredNations = useMemo(() => {
    const lowerCaseSearch = nationSearch.toLowerCase();
    if (!lowerCaseSearch) {
      return nations;
    }

    return nations
      .filter(nation =>
        nation.name.toLowerCase().includes(lowerCaseSearch)
      )
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();

        const aIsExact = aName === lowerCaseSearch;
        const bIsExact = bName === lowerCaseSearch;
        if (aIsExact && !bIsExact) return -1;
        if (!aIsExact && bIsExact) return 1;

        const aStartsWith = aName.startsWith(lowerCaseSearch);
        const bStartsWith = bName.startsWith(lowerCaseSearch);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return a.name.localeCompare(b.name);
      });
  }, [nations, nationSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
       errors.phone = 'A valid contact number is required.';
    }
    if (!formData.fullName.trim()) {
        errors.fullName = 'Full name is required.';
    }
    if (!formData.email) {
      errors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!formData.password) {
      errors.password = 'Password is required.';
    } else if (calculatePasswordStrength(formData.password) < 4) {
      errors.password = 'Password must be at least 8 characters and include an alphabet, a number, and a special character.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    try {
      // Email uniqueness check
      const existingEmailUsers = await searchUserByEmail(formData.email);
      if (existingEmailUsers && existingEmailUsers.length > 0) {
        setFormErrors({ email: 'This email is already registered. Please try logging in.' });
        return;
      }

      if (formData.phone) {
        setIsPhoneChecking(true);
        const existingUsers = await searchUserByPhone(formData.phone);
        setIsPhoneChecking(false);
        if (existingUsers && existingUsers.length > 0) {
          setFormErrors({ phone: 'This phone number is already registered.' });
          return;
        }
      }

      const registrationData = {
        ...formData,
        userId: formData.email,
        email: formData.email,
        password: formData.password,
      };

      await createUserMutation(registrationData);
      navigate('/registration-success');
    } catch (err) {
      // The error is already logged in userApi.js, so we just update the UI.
      setFormErrors({ submit: err.message || 'An error occurred during user creation. Please try again.' });
    }
  };

  const strengthLevels = [
    { label: '', color: '#6c757d' },      // 0: Empty
    { label: 'Weak', color: '#dc3545' },      // 1: Very Weak
    { label: 'Weak', color: '#dc3545' },      // 2: Weak
    { label: 'Medium', color: '#ffc107' },    // 3: Medium
    { label: 'Strong', color: '#28a745' },    // 4: Strong
  ];

  if (user) {
    return (
      <div className="app-container" style={{ background: 'linear-gradient(180deg, #0b3a7a 0%, #0b234a 100%)', minHeight: '100vh' }}>
        <div style={{ padding: '2rem' }}>
          <div className="content-card" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
              You Are Already Logged In
            </h2>
            <p style={{ color: '#333', marginBottom: '1rem' }}>
              You are currently logged in as <strong>{user.attributes.email}</strong>.
            </p>
            <p style={{ color: '#333', marginBottom: '2rem' }}>
              To register a new or different user, please log out first.
            </p>
            <button onClick={handleSignOut} className="btn-primary" style={{ marginRight: '1rem' }}>
              Logout to Register
            </button>
            <button onClick={() => navigate('/home')} className="btn-secondary">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" style={{ background: 'linear-gradient(180deg, #0b3a7a 0%, #0b234a 100%)', minHeight: '100vh' }}>
      <div style={{ padding: '2rem' }}>
        <div className="content-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img src="/sruti-vaibhavam.png" alt="Event Logo" style={{ width: '150px', height: 'auto' }} />
          </div>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
            Registration
          </h2>
          <p style={{ color: '#aaa', marginBottom: '1.5rem', fontSize: '0.9rem', textAlign: 'center' }}>
            Fields marked with <span style={{ color: 'red' }}>*</span> are required.
          </p>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">{t('form.name')} <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                name="fullName"
                className="form-input"
                placeholder={t('form.name')}
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
              {formErrors.fullName && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.fullName}</div>}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Email <span style={{ color: 'red' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  required
                  placeholder="Email"
                />
                {isEmailValid && !isEmailChecking && (
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: 'green', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    ✓
                  </span>
                )}
              </div>
              {isEmailChecking && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0b3a7a', marginTop: '5px', fontSize: '0.8rem' }}>
                  <Loader2 className="animate-spin" size={16} />
                  <span>Checking email...</span>
                </div>
              )}
              {formErrors.email && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.email}</div>}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Password <span style={{ color: 'red' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  required
                  pattern="^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&^()_+\-=\[\]{};':&quot;\\|,.<>\/?]).{8,}$"
                  title="Password must be at least 8 characters and include an alphabet, a number, and a special character."
                />
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer' }} onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={20} color="#333" /> : <Eye size={20} color="#333" />}
                </div>
              </div>
              <div style={{ marginTop: '8px' }}>
                <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                      width: `${passwordStrength * 25}%`,
                      height: '100%',
                      backgroundColor: strengthLevels[passwordStrength].color,
                      transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px' }}>
                  <div style={{ color: '#aaa', fontSize: '0.8rem' }}>
                    Must be at least 8 characters and include a letter, a number, and a special character.
                  </div>
                  {formData.password.length > 0 && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: strengthLevels[passwordStrength].color,
                      textAlign: 'right',
                      height: '1em',
                      fontWeight: 'bold'
                    }}>
                      {strengthLevels[passwordStrength].label}
                    </div>
                  )}
                </div>
              </div>
              {formErrors.password && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.password}</div>}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">{t('form.gender')}</label>
              <div style={{ position: 'relative' }}>
                <select
                  name="gender"
                  className="form-input"
                  value={formData.gender}
                  onChange={handleInputChange}
                  style={{ appearance: 'none', paddingRight: '2rem', background: 'transparent' }}
                >
                  <option value="">{t('form.selectGender', 'Select Gender')}</option>
                  <option value="male">{t('form.male', 'Male')}</option>
                  <option value="female">{t('form.female', 'Female')}</option>
                  <option value="other">{t('form.other', 'Other')}</option>
                </select>
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>⬇️</div>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">{t('form.contact')} <span style={{ color: 'red' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <PhoneInput
                  placeholder={t('form.contact')}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  numberInputProps={{ className: 'form-input' }}
                  defaultCountry="IN"
                />
                {isPhoneValid && !isPhoneChecking && (
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: 'green', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    ✓
                  </span>
                )}
              </div>
              {isPhoneChecking && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0b3a7a', marginTop: '5px', fontSize: '0.8rem' }}>
                  <Loader2 className="animate-spin" size={16} />
                  <span>Checking phone number...</span>
                </div>
              )}
              {formErrors.phone && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.phone}</div>}
            </div>

            <div style={{ marginBottom: '1.5rem' }} ref={nationDropdownRef}>
              <label className="form-label">{t('form.nation')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="nationSearch"
                  className="form-input"
                  placeholder={t('form.searchNation', 'Search for a nation...')}
                  value={nationSearch}
                  onChange={handleNationSearchChange}
                  onFocus={() => setIsNationDropdownOpen(true)}
                  autoComplete="off"
                />
                {isNationDropdownOpen && (
                  <ul style={{ position: 'absolute', width: '100%', background: 'white', border: '1px solid #ccc', borderRadius: '4px', listStyle: 'none', padding: 0, margin: '5px 0 0 0', maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                    {filteredNations.length > 0 ? (
                      filteredNations.map((nation) => (
                        <li key={nation.code} onClick={() => handleNationSelect(nation)} style={{ padding: '10px', cursor: 'pointer', color: '#333' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'} onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}>
                          {nation.name}
                        </li>
                      ))
                    ) : (<li style={{ padding: '10px', color: '#888' }}>No nations found</li>)}
                  </ul>
                )}
              </div>
              {formErrors.country && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.country}</div>}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Address</label>
              <input type="text" name="address" className="form-input" placeholder={t('Address')} value={formData.address} onChange={handleInputChange} />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">{t('Preferred Language')} <span style={{ color: 'red' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <select name="preferredLanguage" className="form-input" value={formData.preferredLanguage} onChange={handleInputChange} required style={{ appearance: 'none', paddingRight: '2rem', background: 'transparent' }}>
                  <option value="">Select Language</option>
                  {languages.map((lang) => (<option key={lang.code} value={lang.code}>{lang.name}</option>))}
                </select>
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>⬇️</div>
              </div>
            </div>

            {/* The `error` object from useMutation is handled by formErrors.submit */}
            {formErrors.submit && <p style={{ color: 'red' }}>{formErrors.submit}</p>}

            <button type="submit" className="btn-primary" disabled={isLoading || isPhoneChecking || isEmailChecking}>
              {isLoading || isPhoneChecking || isEmailChecking ? t('common.loading') : t('common.submit')}
            </button>

            <div className="divider" style={{ marginTop: '1.5rem' }}>
              <span>{t('auth.or', 'or')}</span>
            </div>

            <p style={{ textAlign: 'center', color: '#333', marginTop: '1rem' }}>
              Already have an account?{' '}
              <span onClick={() => navigate('/login')} style={{ color: '#0b3a7a', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}>
                Sign In
              </span>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventRegistrationPage;